/**
 * Where a floating thing goes, so that it is always on the screen.
 *
 * Every menu, every lookup and every picker in the product asks the same
 * question — "I am anchored to this rectangle and I want to be this big;
 * where do I sit?" — and until this file there were three answers to it, two
 * of them wrong in different ways:
 *
 * - `ComboInput` placed itself at `rect.bottom + 4` with no check at all, so a
 *   lookup near the bottom of a flyout ran straight off the window.
 * - `useMenu` computed the room and then did `Math.max(FLOOR, room)`, which
 *   *inflates* it: a trigger with 80px under it got a 220px panel, and 140px
 *   of menu was below the fold on a page that cannot scroll to reach it.
 * - `Popover` was right, but fell back to centring itself and taking the whole
 *   window when neither side was comfortable — so the same picker was
 *   sometimes a dropdown and sometimes a full-screen dialog depending on where
 *   the row happened to be.
 *
 * The rule here is one rule, and it has no special cases:
 *
 * **Below the anchor if below can hold it; above if above is better; capped to
 * whichever side it took; never past an edge.** A panel that cannot have the
 * height it wants scrolls inside itself. It never changes into something else,
 * because a control that behaves differently at the bottom of a page than at
 * the top is a control nobody can learn.
 */

/** What the caller knows: where it is anchored and how big it would like to be. */
export interface PlaceRequest {
  /** The trigger's rectangle, in viewport coordinates. */
  anchor: { left: number; right: number; top: number; bottom: number; width: number }
  /** How wide the panel will be drawn. */
  width: number
  /**
   * How tall it would be if nothing were in its way — its measured height, or
   * 0 before it has been measured once.
   */
  wanted: number
  /** Right-aligned to the anchor rather than left. */
  align?: 'left' | 'right'
  /** Never shorter than this, even if it means overlapping the anchor. */
  floor?: number
  /**
   * On whole pixels. An anchor in a fractional grid puts a panel at
   * x.4375, and Chromium then draws every edge and glyph of it half a pixel
   * off — the visit page's popovers are diffed against their boards pixel for
   * pixel (§23.1). Off by default: every other caller is unchanged.
   */
  snap?: boolean
}

export interface Placement {
  left: number
  top: number
  maxHeight: number
  /** Which way it went — for an arrow, or for a transform origin. */
  above: boolean
}

/** The window's own edges are not a usable surface; leave a margin. */
const MARGIN = 12
/** Between the anchor and the panel, so they read as two things. */
const GAP = 6
/**
 * The shortest panel worth drawing.
 *
 * Below this a panel is a scrollbar with a hint of content, so it is allowed
 * to overlap its anchor rather than be squeezed — the anchor is a field
 * somebody has just finished with, and covering it is the lesser harm.
 */
const FLOOR = 180

export function placeFloating({
  anchor, width, wanted, align = 'left', floor = FLOOR, snap = false,
}: PlaceRequest): Placement {
  const viewportHeight = window.innerHeight
  const viewportWidth = window.innerWidth

  /* Sideways first: clamped so neither edge is crossed, whatever the align. */
  const ideal = align === 'right' ? anchor.right - width : anchor.left
  const left = Math.max(MARGIN, Math.min(ideal, viewportWidth - width - MARGIN))

  const below = viewportHeight - anchor.bottom - GAP - MARGIN
  const above = anchor.top - GAP - MARGIN

  /*
   * Below unless above is genuinely better.
   *
   * "Better" is not "bigger by a pixel": a panel that flips because there are
   * three pixels more above jumps around as the page scrolls. It flips when
   * below cannot hold what the panel wants *and* above can hold more.
   */
  const wantsMore = wanted > 0 && below < wanted
  const goAbove = (wantsMore || below < floor) && above > below

  const room = goAbove ? above : below
  /*
   * The cap is the room — never more. This is the line that was inverted in
   * `useMenu`, and inverting it is how a menu ends up below the fold.
   *
   * When even the better side is under the floor the panel takes the floor
   * and is placed to fit the window, which means it overlaps the anchor. That
   * is deliberate: a 90px menu is not a menu.
   */
  const maxHeight = Math.max(floor, Math.min(room, wanted > 0 ? wanted : room))
  const height = wanted > 0 ? Math.min(wanted, maxHeight) : maxHeight

  const top = goAbove
    ? Math.max(MARGIN, anchor.top - height - GAP)
    : Math.min(anchor.bottom + GAP, viewportHeight - height - MARGIN)

  /* One last clamp, for the overlapping case above. */
  const clamped = Math.max(MARGIN, top)
  return {
    left: snap ? Math.round(left) : left,
    top: snap ? Math.round(clamped) : clamped,
    maxHeight,
    above: goAbove,
  }
}
